import { CalendarIcon } from '@heroicons/react/24/outline';
import { isArray } from 'radash';
import { useCallback, useMemo, useState } from 'react';

import { Grid } from '@/components/core/Grid';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import {useAttributeLabelTranslator} from '@/components/datamodel/hooks/useAttributeLabelTranslator.tsx';
import { DataModelDependentPage } from '@/components/shared';
import {useDataModel} from '@/hooks/useDataModel.tsx';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import { isItemNamespaceReadonly, useOrganization } from '@/hooks/useOrganization';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useBulkSubscriptions } from './bulk-subscriptions.queries';
import { BulkSubscription } from './bulk-subscriptions.types';
import { columns } from './columns';
import { DeleteConfirmationDialog } from './components/DeleteConfirmationDialog';
import { MainPageToolbar } from './components/MainPageToolbar';
import { NewBulkSubscriptionDialog } from './components/NewBulkSubscriptionsDialog';
import { UpdateBulkSubscriptionDialog } from './components/UpdateBulkSubscriptionDialog';
import { UpdateDetailsDialog } from './components/UpdateDetailsDialog';

export default function BulkSubscriptionsPage() {

    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { user } = useUser();
    const { organization } = useOrganization();
    // Active bulk subscriptions mutate items, so recipients of shared items may only read them
    const isActiveReadonly = isItemNamespaceReadonly(organization);

    const [isNewBulkSubscriptionDialogOpen, setIsNewBulkSubscriptionDialogOpen] = useState(false);
    const [isUpdateEntriesDialogOpen, setIsUpdateDialogEntriesOpen] = useState(false);
    const [isUpdateDetailsDialogOpen, setIsUpdateDetailsDialogOpen] = useState(false);

    const [textFilter, setTextFilter] = useState<string | undefined>(undefined);

    const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
    const [entitiesToBeDeleted, setEntitiesToBeDeleted] = useState<BulkSubscription[]>([]);
    // We track the id of the entity being edited rather than a snapshot,
    // so `entityToBeUpdated` always reflects the freshest server state.
    // This matters for the "an import is currently running" callout in
    // `UpdateBulkSubscriptionDialog` — the entity's `status` flips to
    // UPDATING (and back to READY) through real-time messaging while the
    // dialog is open, and the callout only reacts if we re-derive the
    // entity from the refetched list on every render.
    const [entityIdToBeUpdated, setEntityIdToBeUpdated] = useState<number | undefined>();
    const [selectedRows, setSelectedRows] = useState<BulkSubscription[]>([]);

    const pageSize = useDynamicPageSize();
    const {
        data: bulkSubscriptionData,
        isLoading: isLoadingData,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch: refetchData
    } = useBulkSubscriptions({
        variables: {
            limit: pageSize,
            keywords: textFilter
        },

        select: data => data.pages.flatMap(page => page.data),

        enabled: pageSize > 0
    });

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.BULK_SUBSCRIPTION_CHANGED_EVENT,
        eventHandler: () => {
            refetchData();
        }
    });

    const entityToBeUpdated = useMemo(
        () => (entityIdToBeUpdated != null
            ? bulkSubscriptionData?.find(entity => entity.id === entityIdToBeUpdated)
            : undefined),
        [entityIdToBeUpdated, bulkSubscriptionData]
    );

    const onEdit = (entity: BulkSubscription) => {
        setEntityIdToBeUpdated(entity.id);
        setIsUpdateDetailsDialogOpen(true);
    };

    const onUpdateEntries = (entity: BulkSubscription) => {
        setEntityIdToBeUpdated(entity.id);
        setIsUpdateDialogEntriesOpen(true);
    };

    const onDelete = (entityOrEntities: BulkSubscription | BulkSubscription[]) => {
        const entities = isArray(entityOrEntities) ? entityOrEntities : [entityOrEntities];
        setIsDeleteConfirmationDialogOpen(true);
        setEntitiesToBeDeleted(entities);
    };

    const onCreateClose = (newEntity?: BulkSubscription) => {

        if (newEntity) {
            refetchData();
        }

        setIsNewBulkSubscriptionDialogOpen(false);
    };

    const onEditClose = (shouldRefetchData?: boolean) => {

        if (shouldRefetchData) {
            refetchData();
        }

        setIsUpdateDetailsDialogOpen(false);
    };

    const onSelectedRowsChange = useCallback((rowSelection: BulkSubscription[]) => setSelectedRows(rowSelection), []);

    const onInfiniteScroll = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const bulkSubscriptionsColumns = columns({
        t,
        onEdit,
        onDelete,
        onUpdateEntries,
        dataModel,
        translateAttributeLabel,
        isViewer: user?.roles?.contains('bulk_subscription_viewer'),
        isActiveReadonly,
    });

    const isDeleteEnabled = selectedRows.length > 0 &&
        !(isActiveReadonly && selectedRows.some(row => row.active));

    return (
        <DataModelDependentPage>
            <Panel>
                <PanelHeader
                    icon={CalendarIcon}
                    title={t('BULK_SUBSCRIPTIONS')}
                    subTitle={t('BULK_SUBSCRIPTION.SUB_TITLE')} />
                <PanelBody>
                    <Grid
                        toolbar={() => (
                            <MainPageToolbar
                                textFilter={textFilter}
                                onTextFilterChange={setTextFilter}
                                onNewContentClick={() => setIsNewBulkSubscriptionDialogOpen(true)}
                                deleteEnabled={isDeleteEnabled}
                                onDeleteClick={() => onDelete(selectedRows)}
                            />
                        )}
                        columns={bulkSubscriptionsColumns}
                        isWrapContent={true}
                        isLoading={isLoadingData}
                        isLoadingNextPage={isFetchingNextPage}
                        data={bulkSubscriptionData || []}
                        onInfiniteScroll={onInfiniteScroll}
                        onSelectedRowsChange={onSelectedRowsChange}
                        className={`tw-h-[70vh]
                            [&_.table-head]:!tw-h-10 [&_.table-head]:!tw-py-0
                            [&_.scoped-table-cell]:!tw-h-10 [&_.scoped-table-cell]:!tw-py-0
                            [&_th[data-column-id='select']]:!tw-p-0
                            [&_th[data-column-id='select']]:tw-w-10`}
                    />

                    {
                        isNewBulkSubscriptionDialogOpen &&
                        <NewBulkSubscriptionDialog
                            open={isNewBulkSubscriptionDialogOpen}
                            onClose={onCreateClose}
                        />
                    }

                    {
                        isUpdateEntriesDialogOpen && entityToBeUpdated &&
                        <UpdateBulkSubscriptionDialog
                            open={isUpdateEntriesDialogOpen}
                            entity={entityToBeUpdated}
                            onClose={() => setIsUpdateDialogEntriesOpen(false)}
                        />
                    }

                    {
                        isUpdateDetailsDialogOpen && entityToBeUpdated &&
                        <UpdateDetailsDialog
                            open={isUpdateDetailsDialogOpen}
                            entity={entityToBeUpdated}
                            onClose={onEditClose}
                        />
                    }

                    { isDeleteConfirmationDialogOpen &&
                        <DeleteConfirmationDialog
                            open={isDeleteConfirmationDialogOpen}
                            entities={entitiesToBeDeleted}
                            onClose={() => {
                                setEntitiesToBeDeleted([]);
                                setIsDeleteConfirmationDialogOpen(false);
                            }}
                        />
                    }
                </PanelBody>
            </Panel>
        </DataModelDependentPage>
    );
}
